import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from "@mui/material";
import { useCallback, useContext, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Role, User } from "../../app/backendTypes";
import ToolsOverview from "../common/tools/ToolsOverview";
import DisplayUser from "../common/users/DisplayUser";
import UserPicker from "../common/users/UserPicker";
import { HttpClientContext, X_REQUEST_ID } from "../../app/client";
import Morselbar from "../common/Morselbar";
import { useAppSelector } from "../../app/hooks";
import MultiUserPicker from "../common/users/MultiUserPicker";

export default function Users() {
  const isAdminOrEditor = useAppSelector(
    (state) =>
      state.auth.roles.includes(Role.Administrator) ||
      state.auth.roles.includes(Role.CatalogEditor)
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const id = parseInt(searchParams.get("id") || "");

  const [user, setUser] = useState<User | null>(null);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 4,
        width: "100%",
        maxWidth: "md",
      }}
    >
      <br />
      {isAdminOrEditor && <UserPicker
        initialValue={user?.employee.id || id}
        onChange={(value) => {
          setUser(value);

          if (value === null) setSearchParams({});
          else if (id !== value.employee.id)
            setSearchParams({ id: value.employee.id.toString() });
        }}
        onLoad={(value) => {
          setUser(value);

          if (value === null) setSearchParams({});
          else if (id !== value.employee.id)
            setSearchParams({ id: value.employee.id.toString() });
        }}
      />
      }
      <div style={{ display: 'flex', gap: '8px' }}>
        {user && isAdminOrEditor && <Impersonate user={user} />}
      </div>
      {<ResetSlackSession/>}
      {user && isAdminOrEditor && <DisplayUser user={user} />}
      {user && isAdminOrEditor && <ToolsOverview user={user} />}
      <br />
      {<MultiUserSlackReset/>}
    </Box>
  );
}

function Impersonate(props: { user: User }) {
  const { user } = props;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);

  const client = useContext(HttpClientContext);

  const impersonate = useCallback(() => {
    setLoading(true);
    setErrorOpen(false);
    setError(undefined);

    client
      ?.post(`/api/sessions/impersonate?id=${user.employee.id}`)
      .then(() => (window.location.href = window.location.origin))
      .catch((e) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => setLoading(false));
  }, [client, user.employee.id]);

  return (
    <>
      <Button
        color="info"
        variant="contained"
        disabled={loading}
        onClick={impersonate}
      >
        Impersonate
      </Button>

      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to impersonate user"
        error={error}
      />
    </>
  );
}

function MultiUserSlackReset() {
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const client = useContext(HttpClientContext);
  const [user, setUser] = useState<User | null>(null);
  const handleUserPick = (user: User | null) => {
    if (user && !selectedUsers.some((u) => u.employee.id === user.employee.id)) {
      setSelectedUsers((prev) => [...prev, user]);
    }
  };

  const handleRemoveUser = (userId: number) => {
    setSelectedUsers((prev) => prev.filter((u) => u.employee.id !== userId));
  };

  const resetBulkSessions = useCallback(() => {
    setLoading(true);
    setError(undefined);
    setErrorOpen(false);
    setSuccessOpen(false);

    client
      ?.post(`/api/sessions/reset_slack_session_bulk`, {
        users: selectedUsers.map((user) => user.employee.id),
      })
      .then(() => setSuccessOpen(true))
      .catch((e) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => setLoading(false));
  }, [client, selectedUsers]);

  const handleButtonClick = () => {
    setConfirmationOpen(true);
  };

  const handleConfirm = () => {
    setConfirmationOpen(false);
    resetBulkSessions();
  };

  const handleCancel = () => {
    setConfirmationOpen(false);
  };

  return (
    <Card sx={{ width: "100%", maxWidth: "sm", margin: "16px auto" }}>
      <CardHeader
        title="Bulk Slack session reset"
        subheader="Select users and submit them"
      />
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
          <MultiUserPicker initialValue={user?.employee.id} onChange={(user) => { setUser(user); handleUserPick(user); }} />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {selectedUsers.map((user) => (
              <Box
                key={user.employee.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #ccc",
                  padding: "8px",
                  borderRadius: "4px",
                }}
              >
                <span>{user.employee.name}</span>
                <Button
                  color="error"
                  variant="outlined"
                  onClick={() => handleRemoveUser(user.employee.id)}
                >
                  Remove
                </Button>
              </Box>
            ))}
          </Box>
          <Button
            color="primary"
            variant="contained"
            disabled={loading || selectedUsers.length === 0}
            onClick={handleButtonClick}
          >
            Reset bulk sessions
          </Button>
        </Box>
      </CardContent>

      <Dialog open={confirmationOpen} onClose={handleCancel}>
        <DialogTitle>Confirm Bulk Reset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset Slack sessions for the selected users?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirm} color="primary">
            Yes
          </Button>
          <Button onClick={handleCancel} color="secondary">
            No
          </Button>
        </DialogActions>
      </Dialog>


      <Morselbar
        open={successOpen}
        setOpen={setSuccessOpen}
        content="Slack session reset successfully"
        error={undefined}
      />
      
      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to reset user session"
        error={error}
      />
    </Card>
  );
}


function ResetSlackSession() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const client = useContext(HttpClientContext);

  const handleOpenEmailDialog = () => {
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
    setEmail("");
  };

  const handleOpenConfirmationDialog = () => {
    setEmailDialogOpen(false);
    setConfirmationDialogOpen(true);
  };

  const handleCloseConfirmationDialog = () => {
    setConfirmationDialogOpen(false);
  };

  const resetSession = useCallback(() => {
    setLoading(true);
    setError(undefined);
    setErrorOpen(false);
    setSuccessOpen(false);

    client
      ?.post(`/api/sessions/reset_slack_session?email=${email}`)
      .then(() => {
        setSuccessOpen(true);
      })
      .catch((e) => {
        setError(e.response?.headers[X_REQUEST_ID]);
        setErrorOpen(true);
      })
      .finally(() => {
        setLoading(false);
        setConfirmationDialogOpen(false);
        setEmail("");
      });
  }, [client, email]);

  return (
    <>
      <Button
        color="info"
        variant="contained"
        disabled={loading}
        onClick={handleOpenEmailDialog}
      >
        Reset Slack Session
      </Button>

      <Dialog open={emailDialogOpen} onClose={handleCloseEmailDialog}>
        <DialogTitle>Enter Email</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Please enter the email address of the user whose Slack session you
            want to reset.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Email Address"
            type="email"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEmailDialog} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleOpenConfirmationDialog}
            color="primary"
            disabled={!email}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmationDialogOpen}
        onClose={handleCloseConfirmationDialog}
      >
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset the Slack session for{" "}
            <strong>{email}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmationDialog} color="secondary">
            No
          </Button>
          <Button onClick={resetSession} color="primary" disabled={loading}>
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success and Error Notifications */}
      <Morselbar
        open={successOpen}
        setOpen={setSuccessOpen}
        content="Slack session reset successfully"
        error={undefined}
      />
      <Morselbar
        open={errorOpen}
        setOpen={setErrorOpen}
        content="Failed to reset user session"
        error={error}
      />
    </>
  );
}